import { Fragment } from 'react';
import { Check, ChevronRight, Swords, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCodeTornei } from '@/contexts/CodeTorneiContext';
import { useRispostaSfida, type AnnotaSfida, type Nota } from '@/hooks/useRispostaSfida';
import { useNomeGiocoDelleSfide, type SfideNelDm } from '@/hooks/useSfideNelDm';
import { cn } from '@/lib/cn';
import { CHIAVE_COLLEGANDO_PROFILO, nomeDiChiSfida } from '@/lib/limulo/challenge';
import { uscitaDallaCodaAccettando } from '@/lib/limulo/sfidaECoda';
import { SFIDE_TAB_PERCORSO } from '@/lib/limulo/sfideNelDm';
import type { LimuloChallengeWithIdentities } from '@/types/limulo';

/**
 * **La sfida di piattaforma ricevuta, dentro Amici › Messaggi** (T2-5, lotto B3a). Una carta compatta «<nome> ti
 * sfida a <gioco>» con Accetta e Rifiuta — le STESSE azioni della barra e del tab (`useRispostaSfida`: profilo,
 * frasi d'esito, `gestite`, guardia in volo condivisa).
 *
 * `come`: `riga` = un `<li>` in cima all'elenco (chi sfida non ha una conversazione); `carta` = un blocco fisso sotto
 * l'intestazione della conversazione con chi sfida, fuori dallo scorrimento e FUORI dal composer: non copre la casella
 * dei messaggi. Non rende niente se non c'e' nessuna sfida (fuori serratura: il DOM resta quello di prima).
 *
 * 🔑 **Al massimo DUE carte per persona** (`gruppiConTetto`): le eccedenti si riassumono in una riga «altre N sfide» che porta
 * ad Amici › Sfide (`SFIDE_TAB_PERCORSO`) con una NAVIGAZIONE, senza scritture. Il motore ammette fino a 20 sfide in attesa dallo
 * stesso `challenger`: senza tetto, con la tastiera aperta lo scorrimento dei messaggi andrebbe quasi a zero.
 *
 * ⛔ Non apre conversazioni al render: nessuna `openDmConversation`. Rete: le azioni che il giocatore tocca, e UNA scrittura
 * che parte da sola — ogni carta monta `useRispostaSfida`, che dall'I-493 lancia il sync del profilo di gioco
 * (`POST …/sync`, `useSyncProfiloLimulo`): fire-and-forget, una volta per profilo e per sessione, solo per giochi con
 * schema a zero campi. Dal kb#388 Accetta lo aspetta se e' ancora in volo (o se era caduto: ne rifa' UNO) e la carta dice «Sto collegando
 * il tuo profilo…»; Rifiuta non lo aspetta mai. Senza carte non monta niente, quindi nessun sync.
 */
export function SfideRicevuteNelDm({ sfide, come }: { sfide: SfideNelDm; come: 'riga' | 'carta' }) {
  return sfide.ce ? <CarteRicevute sfide={sfide} come={come} /> : null;
}

/** Montato SOLO con almeno una carta: qui, e non nel padre, si legge il catalogo dei giochi (v. `useNomeGiocoDelleSfide`). */
function CarteRicevute({ sfide, come }: { sfide: SfideNelDm; come: 'riga' | 'carta' }) {
  const nomeGioco = useNomeGiocoDelleSfide(sfide.gruppi.flatMap((g) => g.voci.map((v) => v.sfida)));
  const elementi = (
    <>
      {sfide.gruppi.map((g) => (
        <Fragment key={g.chiave}>
          {g.voci.map(({ sfida, nota }) => {
            const n = sfide.note[sfida.challengeId];
            return (
              <CartaSfidaRicevuta
                key={sfida.challengeId}
                come={come}
                sfida={sfida}
                nomeGioco={nomeGioco(sfida)}
                errore={nota === null && n?.tipo === 'errore' ? n.testo : null}
                avviso={nota}
                annota={sfide.annota}
                azzera={sfide.togli}
              />
            );
          })}
          {g.altre > 0 ? <AltreSfide come={come} chiave={g.chiave} quante={g.altre} /> : null}
        </Fragment>
      ))}
    </>
  );
  return come === 'riga' ? (
    elementi
  ) : (
    <div
      data-testid="dm-sfide-ricevute"
      className="flex shrink-0 flex-col gap-2 border-b border-border px-4 py-2"
    >
      {elementi}
    </div>
  );
}

function CartaSfidaRicevuta({
  come,
  sfida,
  nomeGioco,
  errore,
  avviso,
  annota,
  azzera,
}: {
  come: 'riga' | 'carta';
  sfida: LimuloChallengeWithIdentities;
  nomeGioco: string;
  /** L'errore dell'ultimo tocco (tenuto dal padre), a carta ancora offerta. */
  errore: string | null;
  /** Non `null` = la carta non e' piu' offerta: mostra l'esito coi pulsanti spenti. */
  avviso: Nota | null;
  annota: AnnotaSfida;
  azzera: (challengeId: string) => void;
}) {
  const { t } = useTranslation('general');
  const { onAccetta, onRifiuta, accettando, rifiutando, inLettura, collegando } = useRispostaSfida(sfida, annota, azzera);
  const { challengeId } = sfida;
  const nome = nomeDiChiSfida(sfida) ?? t('social.sfide_tab_un_amico');
  const spento = accettando || rifiutando || inLettura || avviso !== null;
  const esito = avviso?.tipo === 'ok' ? avviso.testo : null;
  const testoErrore = avviso !== null && avviso.tipo !== 'ok' ? avviso.testo : errore;
  const Radice = come === 'riga' ? 'li' : 'div';
  // Un avviso, non un dialogo (v. `RigaInArrivo` nel tab): solo su una carta offerta E PULITA, senza esito e senza nessuna
  // frase d'errore a schermo, PRIMA di premere Accetta.
  const { ticket } = useCodeTornei();
  const avvisoCoda = avviso === null && testoErrore === null && uscitaDallaCodaAccettando(ticket, sfida);
  const idAvvisoCoda = `dm-sfida-avviso-coda-${challengeId}`;

  return (
    <Radice
      className={cn('rounded-xl bg-bg-secondary p-3', come === 'riga' && 'mx-4 my-2')}
      data-testid={`dm-sfida-ricevuta-${challengeId}`}
      data-superficie={come === 'riga' ? 'elenco' : 'conversazione'}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted">
          <Swords aria-hidden className="h-4 w-4" />
        </div>
        <p className="min-w-0 flex-1 text-sm font-semibold text-fg">
          {t('social.sfida_ricevuta_testo', { name: nome, game: nomeGioco })}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            disabled={spento}
            onClick={onRifiuta}
            data-testid={`dm-sfida-rifiuta-${challengeId}`}
            aria-label={t('social.sfida_rifiuta_aria', { name: nome, game: nomeGioco })}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted disabled:opacity-50"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={spento}
            onClick={onAccetta}
            data-testid={`dm-sfida-accetta-${challengeId}`}
            aria-describedby={avvisoCoda ? idAvvisoCoda : undefined}
            aria-label={t('social.sfida_accetta_aria', { name: nome, game: nomeGioco })}
            className="flex h-8 items-center gap-1 rounded-full bg-brand px-3 text-xs font-bold text-brand-fg disabled:opacity-50"
          >
            <Check aria-hidden className="h-4 w-4" />
            {t('social.sfide_tab_accetta')}
          </button>
        </div>
      </div>
      {avvisoCoda && (
        <p id={idAvvisoCoda} data-testid={idAvvisoCoda} className="mt-2 text-xs text-fg-muted">
          {t('social.sfida_accetta_avviso_coda', { game: nomeGioco })}
        </p>
      )}
      {collegando && esito === null && testoErrore === null && (
        <p role="status" data-testid={`dm-sfida-collegando-${challengeId}`} className="mt-2 text-xs text-fg-muted">
          {t(CHIAVE_COLLEGANDO_PROFILO)}
        </p>
      )}
      {esito !== null && (
        <p role="status" data-testid={`dm-sfida-esito-${challengeId}`} className="mt-2 text-xs font-medium text-brand">
          {esito}
        </p>
      )}
      {testoErrore !== null && (
        <p role="alert" data-testid={`dm-sfida-errore-${challengeId}`} className="mt-2 text-xs text-danger">
          {testoErrore}
        </p>
      )}
    </Radice>
  );
}

/** «Altre N sfide»: la riga che porta al tab. Una navigazione, mai una scrittura. */
function AltreSfide({ come, chiave, quante }: { come: 'riga' | 'carta'; chiave: string; quante: number }) {
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const Radice = come === 'riga' ? 'li' : 'div';
  return (
    <Radice className={cn(come === 'riga' && 'mx-4 mb-2')}>
      <button
        type="button"
        data-testid={`dm-sfide-altre-${chiave}`}
        onClick={() => navigate(SFIDE_TAB_PERCORSO)}
        className="flex w-full items-center justify-between rounded-xl bg-bg-secondary px-3 py-2 text-xs font-bold text-fg-muted"
      >
        {t('social.sfida_dm_altre', { count: quante })}
        <ChevronRight aria-hidden className="h-4 w-4" />
      </button>
    </Radice>
  );
}
